import clsx from 'clsx';
import { Image } from '@repo/ui/Image';
import { FC } from 'react';

import { MICROCOPY } from '@/data/microcopy/microcopyDictionary';
import { BasicModal } from '@/general/components/BasicModal/BasicModal';
import { getMicrocopyBulk } from '@/stores/microcopyStore';

import styles from './FinanceQuoteBreakdownModal.module.scss';

interface Props {
  id?: string;
  image: string;
  interestRate?: string;
  discountedRateValue?: string;
  repaymentPerMonth?: string;
  discountedRepaymentPerMonth?: string;
  purchasePrice?: string;
  totalDeposit?: string;
  establishmentFee?: string;
  introducerFee?: string;
  ppsrFee?: string;
  providerMonthlyFee?: string;
  totalAmountFinanced?: string;
  earlyTerminationFee?: string;
  discountedIntroducerFee?: string;
  onClose: () => void;
  isOpen: boolean;
  financier: string;
}

export const FinanceQuoteBreakdownModalLegacy: FC<Props> = ({ onClose, isOpen, ...props }) => {
  const checkDiscount =
    !!props.discountedRateValue || !!props.discountedRepaymentPerMonth || !!props.discountedIntroducerFee;
  const {
    header,
    line_1,
    line_2,
    line_3,
    line_4,
    line_5,
    line_6,
    line_7,
    line_8,
    line_9,
    line_10,
    line_11,
    line_12,
    line_13,
    discountedLabel,
  } = getMicrocopyBulk({
    header: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_HEADER],
    line_1: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_1],
    line_2: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_2],
    line_3: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_3],
    line_4: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_4],
    line_5: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_5],
    line_6: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_6],
    line_7: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_7],
    line_8: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_8],
    line_9: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_9],
    line_10: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_10],
    line_11: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_11],
    line_12: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_12],
    line_13: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DETAIL_LINE_13],
    discountedLabel: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_MODAL_DISCOUNTED_LABEL],
  });

  return (
    <BasicModal
      title={header}
      onClose={onClose}
      id={props.id ?? 'finance-fee-details'}
      isOpen={isOpen}
      className={styles.root}
    >
      <div className={styles.contentWrapper}>
        <div
          className={clsx(styles.contentHeader, {
            [styles.spaceBetween]: checkDiscount,
          })}
        >
          <div className={styles.logoImg}>
            <Image src={props.image} width={110} height={25} alt="" />
          </div>
          {checkDiscount && (
            <div className={styles.discountContainer}>
              <p>{discountedLabel}</p>
            </div>
          )}
        </div>
        <div className={styles.content}>
          <div>
            <div className={styles.detailedBlock}>
              <div>
                <div className={styles.bold}>{line_1}</div>
                <div className={styles.bold}>{line_2}</div>
                <div className={styles.description}>{line_3}</div>
              </div>
              <div>
                <div className={styles.price}>
                  <span
                    className={clsx(styles.description, {
                      [styles.oldValues]: !!props.discountedRateValue,
                    })}
                  >
                    {props.interestRate}
                  </span>
                  {props.discountedRateValue && (
                    <span className={styles.discountValue} data-testid="discount-rate-on-modal">
                      {props.discountedRateValue}
                    </span>
                  )}
                </div>
                <div className={styles.price}>
                  <span
                    className={clsx(styles.description, {
                      [styles.oldValues]: !!props.discountedRepaymentPerMonth,
                    })}
                  >
                    {props.repaymentPerMonth}
                  </span>
                  {props.discountedRepaymentPerMonth && (
                    <span className={styles.discountValue} data-testid="discount-repayment-amount">
                      {props.discountedRepaymentPerMonth}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <hr className={styles.divider} />
          <div className={styles.detailedBlock}>
            <div>
              <div className={styles.bold}>{line_4}</div>
              <div className={styles.description}>{line_5}</div>
              <div className={styles.description}>{line_6}</div>
            </div>
            <div className={styles.priceLayout}>
              <div className={styles.description}>{props.purchasePrice}</div>
              <div className={styles.description}>{props.totalDeposit}</div>
            </div>
          </div>
          <hr className={styles.divider} />
          <div className={styles.detailedBlock}>
            <div>
              <div className={styles.bold}>{line_7}</div>
              <div className={styles.description}>{line_8}</div>
              <div className={styles.description}>{line_9}</div>
            </div>
            <div className={styles.priceLayout}>
              <div className={styles.description}>{props.establishmentFee}</div>
              <div className={styles.description}>{props.ppsrFee}</div>
            </div>
          </div>
          <hr className={styles.divider} />
          <div className={styles.detailedBlock}>
            <div className={styles.introducerFeeTitleBlock}>
              <div className={styles.bold}>{line_10}</div>
              {props.financier === 'plenti' && <div className={styles.description}>Payable to Carma</div>}
            </div>
            <div className={styles.introducerFeeBlock}>
              <span
                className={clsx(styles.description, {
                  [styles.oldValues]: !!props.discountedIntroducerFee,
                })}
                data-testid="current-introducer-fee"
              >
                {props.introducerFee}
              </span>
              {props.discountedIntroducerFee && (
                <span className={styles.discountValue} data-testid="discounted-introducer-fee">
                  {props.discountedIntroducerFee}
                </span>
              )}
            </div>
          </div>
          <hr className={styles.divider} />
          <div className={styles.detailedBlock}>
            <div>
              <div className={styles.bold}>{line_11}</div>
            </div>
            <div>
              <div className={styles.description}>{props.providerMonthlyFee}</div>
            </div>
          </div>
          <hr className={styles.divider} />
          <div className={styles.detailedBlock}>
            <div>
              <div className={styles.textPadding}>{line_12}</div>
            </div>
            <div>
              <div className={styles.total}>{props.totalAmountFinanced}</div>
            </div>
          </div>
          <hr className={styles.divider} />
          <div className={styles.detailedBlock}>
            <div>
              <div className={styles.bold}>{line_13}</div>
              <div className={styles.description}>{props.earlyTerminationFee}</div>
            </div>
          </div>
        </div>
      </div>
    </BasicModal>
  );
};
